import { useCallback, useEffect, useState } from "react";

import type { ThemeAppearance } from "@cmmd-forge/contracts/theme";

import { THEME_HALVES_STORAGE_KEY } from "../themeFiles";
import { parseThemeHalves, resolveThemeHalf, type ThemeHalves } from "../themeLibrary";
import { resolveAndApplyTheme } from "../themePalette";

import { useCustomThemes } from "./useCustomThemes";

/**
 * The per-appearance mix ("halves"): which palette owns light, which owns
 * dark, and the applier run that keeps both halves honest.
 *
 * Its own module for one reason: `useTheme` sits at this repo's hook-density
 * ceiling, and the mix is a self-contained unit of state plus exactly one
 * effect. The parent mounts it and hands the results to the settings UI.
 *
 * @module
 */

/** Whether localStorage is usable for the pre-paint and mix reads. */
export function hasThemeStorage(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

/**
 * The stored mix as written, without resolvability pruning. Rebuilding the
 * mix from the pruned parse would silently erase a half whose theme is not
 * resolvable yet, so writes start from here (upstream's readThemeHalvesRaw).
 */
function readStoredThemeHalvesRaw(): { light?: string; dark?: string } {
  if (!hasThemeStorage()) return {};
  try {
    const value: unknown = JSON.parse(
      window.localStorage.getItem(THEME_HALVES_STORAGE_KEY) ?? "null",
    );
    if (value === null || typeof value !== "object" || Array.isArray(value)) return {};
    const halves: { light?: string; dark?: string } = {};
    for (const appearance of ["light", "dark"] as const) {
      const themeId = (value as Record<string, unknown>)[appearance];
      if (typeof themeId === "string") halves[appearance] = themeId;
    }
    return halves;
  } catch {
    return {};
  }
}

function readStoredThemeHalves(): ThemeHalves | null {
  if (!hasThemeStorage()) return null;
  try {
    return parseThemeHalves(window.localStorage.getItem(THEME_HALVES_STORAGE_KEY));
  } catch {
    // An unreadable mix degrades to the base preference, like upstream's.
    return null;
  }
}

/**
 * The stored mix as written, without resolvability pruning: a half naming a
 * theme that is not installed *yet* must survive a write that only touches
 * the other half. Pruning happens on read, in parseThemeHalves.
 */
function writeStoredThemeHalves(halves: { light?: string; dark?: string } | null): void {
  if (!hasThemeStorage()) return;
  try {
    if (halves === null || (halves.light === undefined && halves.dark === undefined)) {
      window.localStorage.removeItem(THEME_HALVES_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_HALVES_STORAGE_KEY, JSON.stringify(halves));
    }
  } catch {
    // Storage failing costs the mix, not the theme; applying continues.
  }
}

/**
 * The next raw mix after handing one appearance to a theme (or taking it
 * back with null). Module-level so the hook's callback stays a thin write.
 */
function nextRawHalves(
  current: { light?: string; dark?: string },
  appearance: ThemeAppearance,
  halfThemeId: string | null,
): { light?: string; dark?: string } {
  const next: { light?: string; dark?: string } = { ...current };
  if (halfThemeId === null) {
    delete next[appearance];
  } else {
    next[appearance] = halfThemeId;
  }
  return next;
}

/** Empty mix means no mix: the base preference owns both appearances. */
function asThemeHalves(raw: { light?: string; dark?: string } | null): ThemeHalves | null {
  return raw !== null && (raw.light !== undefined || raw.dark !== undefined)
    ? (raw as ThemeHalves)
    : null;
}

/**
 * The mix state, its two writers, and the applier run. The parent passes the
 * base preference and the resolved appearance; this module owns everything
 * mix-shaped.
 */
export function useThemeHalves(themeId: string, resolvedTheme: "light" | "dark") {
  const [themeHalves, setThemeHalvesState] = useState<ThemeHalves | null>(() =>
    readStoredThemeHalves(),
  );
  // Library identity, not contents: any install, update, or removal changes
  // it, and the apply effect below keys on it so the page repaints when the
  // active custom theme's colors change in place.
  const customThemes = useCustomThemes();

  /**
   * Hand one appearance of the mix to a theme, or take it back with null.
   * Writes the raw mix (no pruning) and re-applies, so the visible half
   * follows immediately.
   */
  const setThemeHalf = useCallback((appearance: ThemeAppearance, halfThemeId: string | null) => {
    const next = nextRawHalves(readStoredThemeHalvesRaw(), appearance, halfThemeId);
    writeStoredThemeHalves(next);
    setThemeHalvesState(asThemeHalves(next));
    return true;
  }, []);

  /** Clear the whole mix: the base preference owns both appearances again. */
  const clearThemeHalves = useCallback(() => {
    writeStoredThemeHalves(null);
    setThemeHalvesState(null);
    return true;
  }, []);

  // Both axes, not just the palette. A palette is written as inline custom
  // properties on `<html>`, which outrank the light rule *and* the dark rule on
  // that same element — so an inline value cannot vary by `.dark` on its own.
  // Re-applying on `resolvedTheme` is what keeps a themed token flipping with
  // the appearance instead of freezing at whichever half was written first.
  // The per-appearance mix, when one is set, picks which palette owns the
  // appearance being painted; an unset half falls back to the base preference.
  useEffect(() => {
    const appearance: ThemeAppearance = resolvedTheme === "dark" ? "dark" : "light";
    resolveAndApplyTheme(resolveThemeHalf(themeId, themeHalves, appearance), appearance);
  }, [themeId, resolvedTheme, themeHalves, customThemes]);

  return { themeHalves, setThemeHalf, clearThemeHalves };
}
